// Settings (OPENAI.md §4 layout): Account, Text model, Speech model, Reading, Ask, Tags, Sync. Keys live on the
// server; the reader only picks models (when the account may: `/v1/me` features.chooseModels).
import { account } from "../api/account";
import { api, proxyBase } from "../api/client";
import { API_BASE, GOOGLE_CLIENT_ID, IS_PRODUCTION_API } from "../config";
import { ai } from "../levels/aiService";
import { isOpenAI, providerLabel, providerOf, type TextProvider } from "../levels/textClient";
import { canChooseModels, MODEL_CHOICE_LINE, narrationVoice, textModel, askModel } from "../models";
import { narrate } from "../narration/tts";
import { wav } from "../narration/engine";
import { GEMINI_MODELS, GEMINI_VOICES, NARRATION_STYLES, OPENAI_VOICE_MODELS, OPENAI_VOICES, set, settings, voiceLabel, type VoiceProvider } from "../settings";
import { sync } from "../sync/sync";
import { clear, h, icon } from "./dom";

export interface SettingsHost { back(): void; go(page: "text" | "speech" | null): void }

const toggle = (label: string, key: "spoilerCheck" | "prefetchNotes" | "askEnabled" | "askSpeak" | "askResume" | "autoTag" | "useFlex", info?: string) =>
  h("label.setting.toggle", null, h("span", null, label, info ? h("small", null, info) : null),
    h("input", { type: "checkbox", checked: settings[key], onchange: (e: Event) => set(key, (e.target as HTMLInputElement).checked) }));

const nav = (label: string, value: string, onclick: () => void, test?: string) =>
  h("button.setting.nav", { onclick, dataset: test ? { test } : undefined }, h("span", null, label), h("span.value", null, value), h("span.chev", null, "›"));

let gsiInitialized = false;
let gsiTarget: { err: HTMLElement; onDone?: () => void } = { err: document.createElement("div") };

export function signInBlock(onDone?: () => void): HTMLElement {
  const box = h("div.signin");
  const err = h("div.error");
  const gsi = h("div.gsi");
  box.append(gsi);
  // Google Identity Services: the ID token goes to /v1/auth/google (SERVER.md §1).
  const render = () => {
    const g = (window as any).google?.accounts?.id;
    if (!g) return false;
    gsiTarget = { err, onDone };
    if (!gsiInitialized) {
      gsiInitialized = true;
      g.initialize({ client_id: GOOGLE_CLIENT_ID, ux_mode: "popup", auto_select: false, callback: async (r: any) => {
        const t = gsiTarget;
        t.err.textContent = "";
        try { await account.signInWithGoogle(r.credential); t.onDone?.(); } catch (e) { t.err.textContent = e instanceof Error ? e.message : String(e); }
      } });
    }
    g.renderButton(gsi, { theme: "outline", size: "large", shape: "pill", text: "signin_with" });
    return true;
  };
  if (!render()) {
    let s = document.querySelector<HTMLScriptElement>("script[data-gsi]");
    if (!s) {
      s = h("script", { src: "https://accounts.google.com/gsi/client", async: true, defer: true, dataset: { gsi: "1" } });
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => render(), { once: true });
    s.addEventListener("error", () => { gsi.textContent = "Google sign-in couldn't load."; }, { once: true });
  }
  if (account.devLogin) {
    const email = h("input", { type: "email", value: "dev@example.com", "aria-label": "Dev sign-in email", dataset: { test: "dev-email" } });
    box.append(h("form.dev-signin", { onsubmit: async (e: Event) => {
      e.preventDefault(); err.textContent = "";
      try { await account.devSignIn(email.value); onDone?.(); } catch (x) { err.textContent = x instanceof Error ? x.message : String(x); }
    } }, email, h("button.btn", { type: "submit", dataset: { test: "dev-signin" } }, "Dev sign-in")));
  }
  const code = h("input", { placeholder: "ABCD-EFGH", autocapitalize: "characters", autocomplete: "off", spellcheck: false,
    "aria-label": "Sign-in code", dataset: { test: "link-code-input" } });
  box.append(h("form.dev-signin", { onsubmit: async (e: Event) => {
    e.preventDefault(); err.textContent = "";
    try { await account.signInWithCode(code.value); onDone?.(); } catch (x) { err.textContent = x instanceof Error ? x.message : String(x); }
  } }, code, h("button.btn", { type: "submit", dataset: { test: "link-code-signin" } }, "Sign in with a code")),
    h("p.muted.small", null, "No Google on this device? In Depth on a signed-in device, open Settings → Account → Link a device."));
  box.append(err);
  if (!IS_PRODUCTION_API) box.append(h("p.muted.small", null, "Server: " + API_BASE));
  return box;
}

export class SettingsView {
  readonly root = h("div.settings");
  private unsubs: (() => void)[] = [];
  constructor(private host: SettingsHost, private page: "text" | "speech" | null) {}

  mount(parent: HTMLElement) {
    parent.appendChild(this.root);
    this.unsubs.push(account.events.on("changed", () => this.render()), sync.events.on("status", () => { if (!this.page) this.render(); }));
    this.render();
  }
  destroy() { for (const u of this.unsubs) u(); this.root.remove(); }

  private render() {
    clear(this.root);
    if (this.page && !canChooseModels()) this.page = null;
    const title = this.page === "text" ? "Text model" : this.page === "speech" ? "Speech model" : "Settings";
    this.root.append(h("header.page-head", null,
      h("button.icon-btn", { "aria-label": "Back", onclick: () => (this.page ? this.host.go(null) : this.host.back()) }, icon("back")), h("h1", null, title)));
    const body = h("div.settings-body");
    this.root.append(body);
    if (this.page === "text") return this.textPage(body);
    if (this.page === "speech") return this.speechPage(body);

    body.append(h("h2.group", null, "Account"));
    if (account.signedIn) {
      body.append(h("div.setting", null, h("span", null, "Signed in as ", h("b", null, account.user?.email ?? "you")),
        h("button.link", { onclick: () => account.signOut() }, "Sign out")));
      this.linkDevice(body);
    } else body.append(signInBlock());

    body.append(h("h2.group", null, "Models"));
    if (canChooseModels()) {
      body.append(nav("Text model", `${providerLabel(providerOf(textModel()))} · ${textModel()}`, () => this.host.go("text"), "text-model"),
        nav("Speech model", voiceLabel(narrationVoice()), () => this.host.go("speech"), "speech-model"));
    } else body.append(h("p.setting.muted", null, MODEL_CHOICE_LINE));

    body.append(h("h2.group", null, "Reading depth"),
      toggle("Check fiction notes for spoilers", "spoilerCheck", "A second pass rewrites notes against the text so far."),
      toggle("Prepare notes while reading", "prefetchNotes", "Notes for what's on screen are written ahead, so pinching in is instant."),
      h("div.setting", null, h("span", null, "Appearance"), h("div.segmented", null, ...(["system", "light", "dark"] as const).map((a) =>
        h("button", { class: settings.appearance === a ? "on" : "", onclick: () => { set("appearance", a); this.render(); } }, a[0].toUpperCase() + a.slice(1))))));

    body.append(h("h2.group", null, "Ask about this"),
      toggle("Ask about this", "askEnabled"),
      toggle("Speak answers", "askSpeak", "Read answers aloud with the article's voice."),
      toggle("Resume narration after an answer", "askResume"),
      h("div.setting.muted", null, `Answers with ${askModel()}`));

    body.append(h("h2.group", null, "Tags"), toggle("Tag articles automatically", "autoTag", "New links you add here are tagged with the text model."));

    body.append(h("h2.group", null, "Sync"));
    const st = !account.signedIn ? "Signed out" : !account.features.sync ? "Not available for this account" : sync.syncing ? "Syncing…"
      : sync.lastError ? sync.lastError : sync.lastSyncAt ? `Synced ${new Date(sync.lastSyncAt).toLocaleTimeString()}` : "Not synced yet";
    body.append(h("div.setting", null, h("span", null, st), account.features.sync ? h("button.link", { onclick: () => sync.syncNow() }, "Sync now") : null));
    body.append(h("p.muted.small", null, "EPUB books aren't available on the web yet; they stay on your phone."));

    if (account.signedIn && account.features.sync) this.ereader(body);
  }

  // ---- Link a device (SERVER.md §1): a one-time code for a device that can't sign in with Google or Apple

  private link: { code?: string; until?: number; error?: string } = {};

  private linkDevice(body: HTMLElement) {
    const l = this.link;
    const make = async () => {
      const res = await api("/v1/link/code", { method: "POST" }).catch(() => null);
      if (res?.ok) {
        const b = await res.json();
        this.link = { code: b.code, until: Date.now() + b.expiresIn * 1000 };
        setTimeout(() => { if (this.link.code === b.code) { this.link = {}; if (!this.page) this.render(); } }, b.expiresIn * 1000);
      } else this.link = { error: "Couldn't make a code." };
      this.render();
    };
    if (l.code && l.until && l.until > Date.now()) {
      body.append(h("div.setting", null, h("span", null, "Link code"), h("code.value", { dataset: { test: "link-code" }, style: "font-size: 1.3em; letter-spacing: 0.08em" }, l.code)),
        h("p.muted.small", null, `On the other device, choose "Sign in with a code" and enter it. It works once, until ${new Date(l.until).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.`));
    }
    body.append(h("div.setting", null, h("span", null, "Sign in on a device without Google, such as an Android e-reader"),
      h("button.link", { onclick: make, dataset: { test: "link-make" } }, l.code ? "New code" : "Link a device")));
    if (l.error) body.append(h("p.error", null, l.error));
  }

  // ---- E-reader feed (SERVER.md §8): an OPDS catalogue of the library, behind its own password

  private reader: { exists: boolean; password?: string; username?: string; path?: string; error?: string } | null = null;
  private readerLoading = false;

  private ereader(body: HTMLElement) {
    body.append(h("h2.group", null, "E-reader"),
      h("p.muted.small", null, "Browse and download your articles on an e-reader with an OPDS catalogue, such as CrossPoint on the Xteink X4."));
    if (!this.reader) {
      if (!this.readerLoading) {
        this.readerLoading = true;
        api("/v1/reader/password").then(async (r) => { this.reader = r.ok ? await r.json() : { exists: false, error: "Couldn't check the e-reader link." }; })
          .catch(() => { this.reader = { exists: false, error: "Couldn't reach the server." }; })
          .finally(() => { this.readerLoading = false; if (!this.page) this.render(); });
      }
      body.append(h("p.setting.muted", null, "Checking…"));
      return;
    }
    const r = this.reader;
    const create = async () => {
      if (r.exists && !confirm("Make a new private link? E-readers using the old one will stop working.")) return;
      const res = await api("/v1/reader/password", { method: "POST" }).catch(() => null);
      this.reader = res?.ok ? { exists: true, ...(await res.json()) } : { ...r, error: "Couldn't create a link." };
      this.render();
    };
    const revoke = async () => {
      if (!confirm("Turn off the e-reader feed? E-readers using it will be signed out.")) return;
      const res = await api("/v1/reader/password", { method: "DELETE" }).catch(() => null);
      this.reader = res?.ok ? { exists: false } : { ...r, error: "Couldn't turn it off." };
      this.render();
    };
    const row = (label: string, value: string, test: string) =>
      h("div.setting", null, h("span", null, label), h("code.value", { dataset: { test }, style: "user-select: all" }, value));
    if (r.password) {
      body.append(row("Private link", `${API_BASE}${r.path ?? `/opds/k/${r.password}`}`, "reader-url"),
        h("p.muted.small", null, "Shown once. Add it to your e-reader as an OPDS catalogue, with no username or password; anyone with the link can read your articles, so keep it private. On CrossPoint you can paste it into the device's web page (File transfer → OPDS servers)."),
        h("p.muted.small", null, `Readers that ask for a login instead can use ${API_BASE}/opds, username ${r.username ?? account.user?.email ?? "your email"}, password ${r.password}.`));
    } else if (r.exists) {
      body.append(h("p.setting.muted", null, "A private link is set. Make a new one to see it again (the old one stops working)."));
    }
    body.append(h("div.setting", null,
      h("button.link", { onclick: create, dataset: { test: "reader-create" } }, r.exists ? "Make a new link" : "Create private link"),
      r.exists ? h("button.link", { onclick: revoke }, "Turn off") : null));
    if (r.error) body.append(h("p.error", null, r.error));
  }

  // ---- Text model page (OPENAI.md §4)

  private textProvider: TextProvider | null = null;

  private textPage(body: HTMLElement) {
    const p = this.textProvider ?? providerOf(settings.model);
    body.append(h("div.segmented.wide", null, ...(["openrouter", "anthropic", "openai"] as TextProvider[]).map((x) =>
      h("button", { class: x === p ? "on" : "", onclick: () => { this.textProvider = x; this.render(); } }, providerLabel(x)))));
    const why = account.unavailableReason(p);
    if (why) { body.append(h("p.muted", null, why)); }
    const list = h("div.model-list", null, h("p.muted", null, "Loading models…"));
    const search = h("input.search", { type: "search", placeholder: "Search models", oninput: () => draw() });
    const custom = h("form.inline", { onsubmit: (e: Event) => { e.preventDefault(); const v = (custom.querySelector("input") as HTMLInputElement).value.trim(); if (v) this.pickText(v); } },
      h("input", { placeholder: "Or type a model id", value: p === providerOf(settings.model) ? settings.model : "" }), h("button.btn", { type: "submit" }, "Use"));
    body.append(search, list, custom,
      h("h2.group", null, "Per feature"), h("p.muted.small", null, "Each feature can use its own model."),
      h("div.setting", null, h("span", null, "Reading depth & tags"), h("span.value", null, settings.model)),
      h("form.setting.inline", { onsubmit: (e: Event) => { e.preventDefault(); const v = ((e.target as HTMLFormElement).elements.namedItem("ask") as HTMLInputElement).value.trim(); if (v) { set("askModel", v); this.render(); } } },
        h("span", null, "Ask answers"), h("input", { name: "ask", value: settings.askModel }), h("button.btn.small", { type: "submit" }, "Set")));
    let models: { id: string; label: string; price?: string }[] = [];
    const draw = () => {
      clear(list);
      const q = search.value.toLowerCase();
      const shown = models.filter((m) => !q || m.id.toLowerCase().includes(q) || m.label.toLowerCase().includes(q)).slice(0, 200);
      if (!shown.length) list.append(h("p.muted", null, models.length ? "No match." : "No models listed."));
      for (const m of shown) list.append(h("button.model", { class: m.id === settings.model ? "model on" : "model", onclick: () => this.pickText(m.id) },
        h("span", null, m.label, h("small", null, m.id)), m.price ? h("span.value", null, m.price) : null, m.id === settings.model ? icon("check", 16) : null));
    };
    if (!why) loadTextModels(p).then((m) => { models = m; draw(); }).catch((e) => { clear(list); list.append(h("p.muted", null, "Couldn't list models: " + e.message)); });
  }

  /** Picking a model makes it the text model: reading depth (and tags) and Ask (OPENAI.md §4). */
  private pickText(id: string) {
    set("model", id);
    set("askModel", id);
    ai.accessChanged();
    this.render();
  }

  // ---- Speech model page

  private speechPage(body: HTMLElement) {
    const p = settings.provider;
    body.append(h("div.segmented.wide", null, ...(["gemini", "openrouter", "openai"] as VoiceProvider[]).map((x) =>
      h("button", { class: x === p ? "on" : "", onclick: () => { set("provider", x); this.render(); } }, x === "gemini" ? "Gemini" : x === "openai" ? "OpenAI" : "OpenRouter"))));
    const why = account.unavailableReason(p);
    if (why) body.append(h("p.muted", null, why));
    const voiceRow = (id: string, note: string, on: boolean, pick: () => void, model: string) =>
      h("div.model", { class: on ? "model on" : "model" },
        h("button.voice-pick", { onclick: pick }, h("span", null, id, note ? h("small", null, note) : null)),
        h("button.icon-btn.small", { "aria-label": `Preview ${id}`, onclick: () => preview({ provider: p, model, voice: id }) }, icon("play", 16)),
        on ? icon("check", 16) : null);
    if (p === "gemini") {
      body.append(h("h2.group", null, "Model"), ...GEMINI_MODELS.map((m) => h("button.model", { class: settings.geminiModel === m.id ? "model on" : "model",
        onclick: () => { set("geminiModel", m.id); this.render(); } }, h("span", null, m.label), settings.geminiModel === m.id ? icon("check", 16) : null)));
      body.append(h("h2.group", null, "Voice"), ...GEMINI_VOICES.map(([v, n]) => voiceRow(v, n, settings.voiceName === v, () => { set("voiceName", v); this.render(); }, settings.geminiModel)));
      body.append(toggle("Half-price narration (Flex)", "useFlex", "Parts ahead are narrated on Gemini's Flex tier."));
    } else if (p === "openai") {
      body.append(h("h2.group", null, "Model"), ...OPENAI_VOICE_MODELS.map((m) => h("button.model", { class: settings.openAIModel === m.id ? "model on" : "model",
        onclick: () => { set("openAIModel", m.id); this.render(); } }, h("span", null, m.label), h("span.value", null, m.hour), settings.openAIModel === m.id ? icon("check", 16) : null)));
      body.append(h("h2.group", null, "Voice"), ...OPENAI_VOICES.map((v) => voiceRow(v, v === "marin" || v === "cedar" ? "Best" : "", settings.openAIVoice === v, () => { set("openAIVoice", v); this.render(); }, settings.openAIModel)));
    } else {
      const list = h("div.model-list", null, h("p.muted", null, "Loading speech models…"));
      body.append(h("h2.group", null, "Model"), list);
      if (!why) loadSpeechModels().then((models) => {
        clear(list);
        for (const m of models) {
          list.append(h("button.model", { class: settings.openRouterModel === m.id ? "model on" : "model", onclick: () => {
            set("openRouterModel", m.id);
            if (m.voices.length && !m.voices.includes(settings.openRouterVoice)) set("openRouterVoice", m.voices[0]);
            if (!m.voices.length) set("openRouterVoice", "");
            this.render();
          } }, h("span", null, m.name, h("small", null, m.id)), settings.openRouterModel === m.id ? icon("check", 16) : null));
        }
        const cur = models.find((m) => m.id === settings.openRouterModel);
        if (cur) body.append(h("h2.group", null, "Voice"), ...(cur.voices.length ? cur.voices : [""]).map((v) =>
          voiceRow(v || "Default", "", settings.openRouterVoice === v, () => { set("openRouterVoice", v); this.render(); }, cur.id)));
      }).catch((e) => { clear(list); list.append(h("p.muted", null, "Couldn't list models: " + e.message)); });
    }
    if (p !== "openrouter") {
      body.append(h("h2.group", null, "Narration style"), h("div.segmented.wrap", null, ...NARRATION_STYLES.map(([label, text]) =>
        h("button", { class: settings.narrationStyle === text ? "on" : "", onclick: () => { set("narrationStyle", text); this.render(); } }, label))));
    }
    body.append(h("p.muted.small", null, "Articles keep the voice they were started with; this applies to new narration."));
  }
}

async function loadTextModels(p: TextProvider): Promise<{ id: string; label: string; price?: string }[]> {
  if (p === "openrouter") {
    const r = await api(`${proxyBase("openrouter")}/api/v1/models`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = (await r.json()).data ?? [];
    return data.filter((m: any) => (m.architecture?.output_modalities ?? ["text"]).includes("text"))
      .map((m: any) => ({ id: m.id, label: m.name ?? m.id, price: m.pricing?.prompt ? `$${(Number(m.pricing.prompt) * 1e6).toFixed(2)} / M input` : undefined }))
      .sort((a: any, b: any) => (a.id === settings.model ? -1 : b.id === settings.model ? 1 : 0));
  }
  if (p === "anthropic") {
    const r = await api(`${proxyBase("anthropic")}/v1/models`, { headers: { "anthropic-version": "2023-06-01" } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return ((await r.json()).data ?? []).map((m: any) => ({ id: m.id, label: m.display_name ?? m.id }));
  }
  const r = await api(`${proxyBase("openai")}/v1/models`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const bad = ["tts", "transcribe", "realtime", "audio", "image", "search", "deep-research", "codex", "-pro", "computer-use", "moderation", "embedding", "live"];
  return ((await r.json()).data ?? []).filter((m: any) => isOpenAI(m.id) && !bad.some((b) => m.id.includes(b)) && !/-\d{4}-\d{2}-\d{2}$/.test(m.id))
    .sort((a: any, b: any) => (b.created ?? 0) - (a.created ?? 0)).map((m: any) => ({ id: m.id, label: m.id }));
}

async function loadSpeechModels(): Promise<{ id: string; name: string; voices: string[] }[]> {
  const r = await api(`${proxyBase("openrouter")}/api/v1/models?output_modalities=speech`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return ((await r.json()).data ?? []).map((m: any) => ({ id: m.id, name: m.name ?? m.id, voices: m.supported_voices ?? [] }));
}

let previewEl: HTMLAudioElement | null = null;
async function preview(v: { provider: VoiceProvider; model: string; voice: string }) {
  previewEl?.pause();
  try {
    const pcm = await narrate("This is how I sound reading your articles.", v, { style: settings.narrationStyle, signal: AbortSignal.timeout(60_000) });
    previewEl = new Audio(URL.createObjectURL(wav(pcm, 24000)));
    await previewEl.play();
  } catch (e) { alert("Preview failed: " + (e instanceof Error ? e.message : String(e))); }
}
